/*
 * TICKET LIST — the logged in customer's support tickets on the help page, for any theme.
 * Only where the sale channel shows them (Prodyflow settings/sale channels: show_tickets); else the
 * engine and the webshop API answer 403 and the list stays empty.
 *
 * All logic is here (loading, the state of every ticket, its topic, its order, the dates); the theme gives
 * the in-DOM Vue template:
 *   <div data-ticket-app data-delimiters="[[,]]" data-texts="#json-script-id" data-orders-url="/hu/shop/profile">
 *     <div v-if="tickets === null">loading</div>
 *     <ul v-else><li v-for="tk in tickets" :key="tk.id"> ... [[ tk.title ]] [[ statusLabel(tk) ]] ... </li></ul>
 *   </div>
 * Data for the markup: tickets (null while loading, then the list, newest first), error, ordersUrl.
 * Methods: t(key, fallback), statusLabel(tk), statusClass(tk), categoryName(tk), orderLabel(tk),
 * dateLabel(value). A ticket: id, title, status (open | in_progress | resolved | closed), category names,
 * order_id, order_pretty_id, created_at, updated_at, resolved_at.
 */
const TicketList = (() => {
    let app = null;
    let root = null;

    const token = () => (window.Cookies && Cookies.getItem('login_token')) || '';
    const headers = () => (window.http && typeof http.headers === 'function') ? http.headers() : { 'Content-Type': 'application/json', Accept: 'application/json' };

    const component = (el) => {
        let texts = {};
        try {
            const src = el.dataset.texts ? document.querySelector(el.dataset.texts) : null;
            texts = src ? JSON.parse(src.textContent) : {};
        } catch (e) { texts = {}; }
        const lan = () => window.lan || document.documentElement.lang || 'en';
        return {
            data() {
                return { texts, tickets: null, error: '', ordersUrl: el.dataset.ordersUrl || '' };
            },
            methods: {
                t(key, fallback) { return this.texts[key] ?? fallback ?? ''; },
                statusLabel(tk) { return this.t('status_' + tk.status, tk.status); },
                // for the badge's colour: open | in_progress | resolved | closed
                statusClass(tk) { return 'is-' + String(tk.status || '').replace(/_/g, '-'); },
                categoryName(tk) {
                    const n = tk.category_names || {};
                    return n[lan()] || n.en || Object.values(n)[0] || '';
                },
                orderLabel(tk) { return tk.order_pretty_id || (tk.order_id ? '#' + tk.order_id : ''); },
                dateLabel(value) {
                    if (!value) return '';
                    try { return new Intl.DateTimeFormat(lan(), { year: 'numeric', month: 'short', day: 'numeric' }).format(new Date(value)); }
                    catch (e) { return String(value).slice(0, 10); }
                },
                async load() {
                    if (!token()) { this.tickets = []; return; }
                    try {
                        const r = await fetch('/api/tickets', { method: 'POST', headers: headers(), body: JSON.stringify({ token: token() }) });
                        // 403: the sale channel does not show tickets - nothing to list
                        if (r.status === 403) { this.tickets = []; return; }
                        if (!r.ok) throw new Error('tickets');
                        const d = await r.json();
                        this.tickets = Array.isArray(d.data) ? d.data : [];
                    } catch (e) {
                        this.tickets = [];
                        this.error = this.t('error', 'Could not load your tickets.');
                    }
                },
            },
            mounted() { this.load(); },
        };
    };

    const mount = () => {
        if (root) return root;
        const el = document.querySelector('[data-ticket-app]');
        if (!el || typeof Vue === 'undefined') return null;
        const comp = component(el);
        if (el.dataset.delimiters) comp.delimiters = el.dataset.delimiters.split(',');
        app = Vue.createApp(comp);
        root = app.mount(el);
        return root;
    };

    const boot = () => setTimeout(mount, 0);
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();

    return { mount, get app() { return root; } };
})();

window.TicketList = TicketList;
